import {
  type AppointmentMode,
  type AppointmentStatus,
  type ConnectionStatus,
  type FollowupDto,
  type FollowupVariant,
  type LeadSource,
  type LeadStage,
  type MessageStatus,
  type ProposalDto,
  type ProposalStatus,
  type TemplateStatus,
  type WorkspaceRole,
  zonedParts,
} from "@repo/polaris-shared";

const dateFormatter = new Intl.DateTimeFormat("it-IT", { dateStyle: "medium", timeStyle: "short" });
const shortTimeFormatter = new Intl.DateTimeFormat("it-IT", { hour: "2-digit", minute: "2-digit" });
const relativeFormatter = new Intl.RelativeTimeFormat("it-IT", { numeric: "auto" });

export function formatDate(value: string | null | undefined) {
  if (!value) return "Mai";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Data non disponibile" : dateFormatter.format(date);
}

export function formatTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : shortTimeFormatter.format(date);
}

const weekdayFormatter = new Intl.DateTimeFormat("it-IT", { weekday: "short" });
const shortDateFormatter = new Intl.DateTimeFormat("it-IT", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
});
const dayFormatter = new Intl.DateTimeFormat("it-IT", {
  weekday: "long",
  day: "numeric",
  month: "long",
});

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** WhatsApp list style: the hour today, "Ieri", the weekday this week, then the date. */
export function formatListTime(value: string, now = new Date()) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (days <= 0) return shortTimeFormatter.format(date);
  if (days === 1) return "Ieri";
  if (days < 7) return weekdayFormatter.format(date);
  return shortDateFormatter.format(date);
}

/** The day divider of a chat: "Oggi", "Ieri", "lunedì 5 ottobre". */
export function formatChatDay(value: string, now = new Date()) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (days === 0) return "Oggi";
  if (days === 1) return "Ieri";
  const label = dayFormatter.format(date);
  return date.getFullYear() === now.getFullYear() ? label : `${label} ${date.getFullYear()}`;
}

/** How long since then, without "fa": "5 minuti", "2 ore", "3 giorni". */
export function formatElapsed(value: string, now = Date.now()) {
  const minutes = Math.max(0, Math.round((now - Date.parse(value)) / 60_000));
  if (!Number.isFinite(minutes)) return "";
  if (minutes < 1) return "meno di un minuto";
  if (minutes < 60) return minutes === 1 ? "1 minuto" : `${minutes} minuti`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours === 1 ? "1 ora" : `${hours} ore`;
  const days = Math.round(hours / 24);
  return days === 1 ? "1 giorno" : `${days} giorni`;
}

const monthDayFormatter = new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "short" });
const monthDayYearFormatter = new Intl.DateTimeFormat("it-IT", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

/** A table's last activity: "6 minuti fa" within 24 hours, then "Ieri 18:40", "lun 18:40", "5 ott". */
export function formatActivity(value: string, now = new Date()) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const minutes = Math.max(0, Math.round((now.getTime() - date.getTime()) / 60_000));
  if (minutes < 1) return "Adesso";
  if (minutes < 60) return relativeFormatter.format(-minutes, "minute");
  if (minutes < 24 * 60) return relativeFormatter.format(-Math.round(minutes / 60), "hour");
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  const time = shortTimeFormatter.format(date);
  if (days === 1) return `Ieri ${time}`;
  if (days < 7) return `${weekdayFormatter.format(date)} ${time}`;
  return date.getFullYear() === now.getFullYear()
    ? monthDayFormatter.format(date)
    : monthDayYearFormatter.format(date);
}

export function formatRelative(value: string) {
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return "";
  const delta = time - Date.now();
  const minutes = Math.round(delta / 60_000);
  if (Math.abs(minutes) < 60) return relativeFormatter.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return relativeFormatter.format(hours, "hour");
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 30) return relativeFormatter.format(days, "day");
  return formatDate(value);
}

export const leadStageLabel: Record<LeadStage, string> = {
  new: "Nuovo",
  contacted: "Contattato",
  qualified: "Qualificato",
  proposal: "Proposta",
  won: "Acquisito",
  lost: "Perso",
  unqualified: "Non qualificato",
  archived: "Archiviato",
};

export const leadSourceLabel: Record<LeadSource, string> = {
  manual: "Inserimento manuale",
  whatsapp: "WhatsApp",
  meta_lead_ads: "Meta Lead Ads",
  import: "Importazione",
  instagram: "Instagram",
  messenger: "Messenger",
  sityweb: "Sityweb",
};

export const roleLabel: Record<WorkspaceRole, string> = {
  owner: "Titolare",
  admin: "Amministratore",
  operator: "Operatore",
  reviewer: "Revisore",
};

export const connectionStatusLabel: Record<ConnectionStatus, string> = {
  active: "Attiva",
  pending: "Da completare",
  expired: "Scaduta",
  revoked: "Revocata",
  error: "Richiede attenzione",
};

export const messageStatusLabel: Record<MessageStatus, string> = {
  queued: "In coda",
  sent: "Inviato",
  delivered: "Consegnato",
  read: "Letto",
  failed: "Invio non riuscito",
  received: "Ricevuto",
};

export const proposalStatusLabel: Record<ProposalStatus, string> = {
  pending_approval: "Da approvare",
  approved: "Approvata",
  scheduled: "Programmata",
  running: "In esecuzione",
  completed: "Completata",
  rejected: "Rifiutata",
  cancelled: "Annullata",
  failed: "Non riuscita",
  expired: "Scaduta",
  superseded: "Sostituita",
};

/** A connector action can finish in Polaris while the requested site work is still underway. */
export function proposalDisplayStatus(proposal: Pick<ProposalDto, "kind" | "status">): string {
  if (proposal.kind === "connector_action") {
    if (proposal.status === "approved") return "In coda";
    if (proposal.status === "completed") return "Richiesta inviata";
  }
  return proposalStatusLabel[proposal.status];
}

export const templateStatusLabel: Record<TemplateStatus, string> = {
  draft: "Bozza",
  pending: "In revisione",
  approved: "Approvato",
  rejected: "Rifiutato",
  paused: "In pausa",
  disabled: "Disabilitato",
};

/** Groups an E.164 number for reading: Italian mobiles as +39 333 123 4567. */
export function formatPhone(value: string | null | undefined): string | null {
  if (!value) return null;
  const digits = value.replace(/\D/g, "");
  if (!value.trim().startsWith("+") || digits.length < 8) return value;
  if (digits.startsWith("39")) {
    const national = digits.slice(2);
    return national.startsWith("3") && national.length === 10
      ? `+39 ${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`
      : `+39 ${national.slice(0, 2)} ${national.slice(2)}`;
  }
  return `+${digits}`;
}

/** First letters of the first two words; emoji, numbers and symbols are skipped. */
export function initials(name: string) {
  return name
    .split(/\s+/)
    .map((part) => part.match(/\p{L}/u)?.[0])
    .filter(Boolean)
    .slice(0, 2)
    .map((letter) => letter?.toUpperCase())
    .join("");
}

/** A stable hue (0–359) for a lead, so the same contact keeps the same color. */
export function avatarHue(seed: string) {
  // FNV-1a: tiny, deterministic and well spread for short strings.
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) % 360;
}

export function makeIdempotencyKey() {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;
}

export function safeHttpsUrl(value: string | null | undefined) {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

const DAY_MS = 24 * 60 * 60 * 1_000;
const dueDayFormatters = new Map<string, Intl.DateTimeFormat>();

function cachedFormatter(
  cache: Map<string, Intl.DateTimeFormat>,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
) {
  let formatter = cache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("it-IT", { ...options, timeZone });
    cache.set(timeZone, formatter);
  }
  return formatter;
}

/** Calendar days from `now` to `value` in `timeZone` (negative in the past). */
const dueWeekdayFormatters = new Map<string, Intl.DateTimeFormat>();

function calendarDayDistance(value: Date, now: Date, timeZone: string) {
  const day = (instant: Date) => {
    const parts = zonedParts(instant, timeZone);
    return Date.UTC(parts.year, parts.month - 1, parts.day);
  };
  return Math.round((day(value) - day(now)) / DAY_MS);
}

/** Date and time in the workspace zone, e.g. "lun 12 ott, 09:00". */
export function formatZonedDate(value: string | Date, timeZone: string) {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "Data non disponibile";
  return cachedFormatter(dueDayFormatters, timeZone, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/** Day in the workspace zone, e.g. "lun 12 ott". */
export function formatZonedDay(value: string | Date, timeZone: string) {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "Data non disponibile";
  return cachedFormatter(dueWeekdayFormatters, timeZone, {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(date);
}

/** A `YYYY-MM-DD` day, e.g. "martedì 20 ottobre": the day of "Sposta" (ADR 0055). */
export function formatIsoDay(value: string | null) {
  const date = value ? new Date(`${value}T12:00:00Z`) : null;
  if (!date || Number.isNaN(date.getTime())) return "un giorno da scegliere";
  return new Intl.DateTimeFormat("it-IT", {
    timeZone: "UTC",
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(date);
}

/**
 * When a follow-up is due, relative to today in the workspace zone. A
 * follow-up is due for its whole day (ADR 0055): no hour, and it is late only
 * from the next day.
 */
export function formatDue(value: string, timeZone: string, now = new Date()) {
  const due = new Date(value);
  if (Number.isNaN(due.getTime())) return { label: "Data non disponibile", overdue: false };
  const days = calendarDayDistance(due, now, timeZone);
  const overdue = days < 0;
  if (days < -1) return { label: `In ritardo di ${-days} giorni`, overdue };
  if (days === -1) return { label: "In ritardo da ieri", overdue };
  if (days === 0) return { label: "Da fare oggi", overdue };
  if (days === 1) return { label: "Domani", overdue };
  return { label: formatZonedDay(due, timeZone), overdue };
}

export const followupVariantLabel: Record<FollowupVariant, string> = {
  generic: "Generico",
  preview: "Dopo la bozza",
  custom: "Personalizzato",
};

export function followupStepLabel(followup: Pick<FollowupDto, "origin" | "kind" | "step">) {
  if (followup.kind === "decide") return "Da decidere";
  if (followup.origin === "member") return "Programmato";
  if (followup.origin === "ai") return "Proposto dall'AI";
  if (followup.step === 1) return "1° follow-up";
  if (followup.step === 2) return "2° follow-up";
  return "Nuovo tentativo";
}

export const appointmentModeLabel: Record<AppointmentMode, string> = {
  call: "Chiamata",
  video: "Videochiamata",
  in_person: "Di persona",
  other: "Altro",
};

export const appointmentStatusLabel: Record<AppointmentStatus, string> = {
  scheduled: "In programma",
  done: "Fatto",
  missed: "Non ha risposto",
  cancelled: "Annullato",
};

const clockFormatters = new Map<string, Intl.DateTimeFormat>();

/** Hour and minutes in the workspace zone, e.g. "15:00". */
export function formatZonedClock(value: string | Date, timeZone: string) {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "";
  return cachedFormatter(clockFormatters, timeZone, { hour: "2-digit", minute: "2-digit" }).format(
    date,
  );
}

const numericDayFormatters = new Map<string, Intl.DateTimeFormat>();

/** "oggi 10:30", "domani 10:30", "26/10 10:30": a reminder in little space (owner, 22/10). */
export function formatShortWhen(value: string, timeZone: string, now = new Date()) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const days = calendarDayDistance(date, now, timeZone);
  const day =
    days === 0
      ? "oggi"
      : days === 1
        ? "domani"
        : cachedFormatter(numericDayFormatters, timeZone, {
            day: "2-digit",
            month: "2-digit",
          }).format(date);
  return `${day} ${formatZonedClock(date, timeZone)}`;
}

const shortDayFormatters = new Map<string, Intl.DateTimeFormat>();

/** "mer 28/10": a day in a short line, in the workspace zone (owner, 2026-10-26). */
export function formatShortDay(value: string | Date, timeZone: string) {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "";
  return cachedFormatter(shortDayFormatters, timeZone, {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  }).format(date);
}

/**
 * The next follow-up in one line, before its day (owner, 2026-10-26): "mer 28/10 (2°)",
 * "mer 28/10 · da decidere". The automatic ones are due for their whole day: no hour.
 */
export function nextFollowupWhen(
  followup: Pick<FollowupDto, "dueAt" | "origin" | "kind" | "step">,
  timeZone: string,
) {
  const day = formatShortDay(followup.dueAt, timeZone);
  if (followup.kind === "decide") return `${day} · da decidere`;
  if (followup.origin === "ai") return `${day} · proposto dall'AI`;
  if (followup.step === 1 || followup.step === 2) return `${day} (${followup.step}°)`;
  return `${day} · nuovo tentativo`;
}

/** «FU 28/10» in a Posta row: the day of the pending follow-up, an overdue one apart. */
export function followupBadge(dueAt: string, timeZone: string, now = new Date()) {
  const date = new Date(dueAt);
  const days = calendarDayDistance(date, now, timeZone);
  const day = cachedFormatter(numericDayFormatters, timeZone, {
    day: "2-digit",
    month: "2-digit",
  }).format(date);
  if (days < 0)
    return {
      label: `FU ${day}`,
      state: "overdue" as const,
      title: `Follow-up in ritardo dal ${day}`,
    };
  if (days === 0)
    return { label: "FU oggi", state: "today" as const, title: "Follow-up da fare oggi" };
  return {
    label: `FU ${day}`,
    state: "later" as const,
    title: `Prossimo follow-up: ${formatShortDay(date, timeZone)}`,
  };
}

/** "15:00–15:30" in the workspace zone. */
export function formatTimeRange(startsAt: string, endsAt: string, timeZone: string) {
  return `${formatZonedClock(startsAt, timeZone)}–${formatZonedClock(endsAt, timeZone)}`;
}

/** "gio 15 ott, 15:00–15:30": when an appointment is, in the workspace zone. */
export function formatAppointmentWhen(startsAt: string, endsAt: string, timeZone: string) {
  return `${formatZonedDay(startsAt, timeZone)}, ${formatTimeRange(startsAt, endsAt, timeZone)}`;
}

/** "30 min", "1 h", "1 h 30". */
export function formatDuration(minutes: number) {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest}` : `${hours} h`;
}

/** How far the start of an appointment is: "tra 15 minuti", "adesso", "iniziato da 5 minuti". */
export function formatStartsIn(startsAt: string, now = Date.now()) {
  const minutes = Math.round((new Date(startsAt).getTime() - now) / 60_000);
  if (minutes > 90) return `tra ${Math.round(minutes / 60)} ore`;
  if (minutes > 1) return `tra ${minutes} minuti`;
  if (minutes === 1) return "tra 1 minuto";
  if (minutes >= -1) return "adesso";
  return `iniziato da ${-minutes} minuti`;
}

/** Names of the extra fields Polaris writes itself; the form's own keep their key. */
const leadFieldLabels: Record<string, string> = { attribution: "Provenienza (Meta)" };
/** The Meta ids of where a Lead Ads lead came from, saved as `attribution`. */
const leadFieldPartLabels: Record<string, string> = {
  campaignId: "Campagna",
  adsetId: "Gruppo di inserzioni",
  adId: "Inserzione",
  formId: "Modulo",
};

export function leadFieldLabel(key: string) {
  return leadFieldLabels[key] ?? key;
}

/** An extra field as text: a group of values (the ad attribution) one per part, not "[object Object]". */
export function formatLeadField(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value !== "object") return String(value);
  const parts = Object.entries(value as Record<string, unknown>)
    .filter(([, part]) => part !== null && part !== undefined && part !== "")
    .map(
      ([key, part]) =>
        `${leadFieldPartLabels[key] ?? key}: ${typeof part === "object" ? JSON.stringify(part) : String(part)}`,
    );
  return parts.length ? parts.join(" · ") : "—";
}
